import { useEffect, useState } from 'react';
import { sortByMetric, filterByValues } from '../../shared/utils/helpers';
import { EntityTypes } from 'context/entityContext';

const toConsumptionValues: { [key: string]: string[] } = {
  track: ['streaming', 'track_downloads'],
  release: ['streaming', 'album_downloads', 'physical'],
  artist: ['streaming', 'album_downloads', 'track_downloads', 'physical'],
  project: ['streaming', 'album_downloads', 'track_downloads', 'physical'],
};

export default useOverallBreakdownData;

interface ILabelValuesPair {
  label: string;
  values: any[];
}

/*
 * This hook creates and returns an array of objects
 * with label value pairs based on the active metric
 *
 * @param {Array} breakdowns the breakdowns such as 'Streaming'
 * @param {String} activeMetric which metric value to get.
 * @returns {Array} normalized breakdowns and scale callback
 */
function useOverallBreakdownData(
  breakdowns: any[] | undefined,
  activeMetric: string,
  entityType: EntityTypes
): any[] | undefined {
  const [breakdownRows, setBreakdownRows] = useState<any[] | undefined>();

  useEffect(() => {
    if (!breakdowns) return;
    const allowedValues = toConsumptionValues[entityType];
    const sortByActiveMetric = sortByMetric(activeMetric);
    const filterAllowedValues = filterByValues(allowedValues);
    const mapToPair = mapToLabelValuePair(entityType);

    const rows = breakdowns
      .filter(filterAllowedValues)
      .map(mapToPair)
      .sort(sortByActiveMetric);

    setBreakdownRows(rows);
  }, [breakdowns, activeMetric, entityType]);

  return breakdownRows;
}

function mapToLabelValuePair(
  entityType: EntityTypes
): (breakdown: any) => ILabelValuesPair {
  return function (breakdown: any): ILabelValuesPair {
    return {
      label: getLabel(breakdown.breakdown, entityType),
      values: breakdown?.data?.[0].data,
    };
  };
}

// When we are on a artist or project page, we have track AND release downloads.
// When we are on a track page, it's redundant to say 'Track | Downloads' so we say 'Downloads'.
function getLabel(str: string, entityType: EntityTypes): string {
  const isSplitDownload = entityType === 'artist' || entityType === 'project';

  if (isSplitDownload) {
    if (str === 'track_downloads') return 'download | tracks';
    if (str === 'album_downloads') return 'download | releases';
  }

  if (str === 'track_downloads') return 'download';
  if (str === 'album_downloads') return 'download';

  return str;
}
